/**
 * Cookie consent preferences (GDPR / ePrivacy / CCPA-friendly).
 * Stores the user's choice in localStorage with a 12-month expiry.
 */

export const COOKIE_CONSENT_STORAGE_KEY = 'aacore_cookie_consent_v1';
export const COOKIE_CONSENT_VERSION = 1;
export const COOKIE_CONSENT_TTL_MS = 365 * 24 * 60 * 60 * 1000;

export const COOKIE_PREFERENCES_OPEN_EVENT = 'aacore:open-cookie-preferences';

export interface CookieConsentPreferences {
  /** Required for sign-in, security, and core site functionality. Always enabled. */
  necessary: true;
  /** Google Analytics usage measurement. Opt-in only. */
  analytics: boolean;
}

export interface CookieConsentRecord {
  version: number;
  timestamp: number;
  preferences: CookieConsentPreferences;
}

const defaultPreferences = (analytics: boolean): CookieConsentPreferences => ({
  necessary: true,
  analytics,
});

export function getCookieConsent(): CookieConsentRecord | null {
  try {
    const raw = localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
    if (!raw) return null;

    const record = JSON.parse(raw) as CookieConsentRecord;
    if (record.version !== COOKIE_CONSENT_VERSION) return null;
    if (!record.preferences || record.preferences.necessary !== true) return null;

    const elapsed = Date.now() - record.timestamp;
    if (elapsed > COOKIE_CONSENT_TTL_MS) {
      localStorage.removeItem(COOKIE_CONSENT_STORAGE_KEY);
      return null;
    }

    return record;
  } catch {
    return null;
  }
}

export function hasCookieConsentDecision(): boolean {
  return getCookieConsent() !== null;
}

export function saveCookieConsent(preferences: CookieConsentPreferences): CookieConsentRecord {
  const record: CookieConsentRecord = {
    version: COOKIE_CONSENT_VERSION,
    timestamp: Date.now(),
    preferences: {
      necessary: true,
      analytics: preferences.analytics,
    },
  };

  try {
    localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(record));
  } catch {
    // If storage is unavailable, still return the in-memory record for this session.
  }

  window.dispatchEvent(
    new CustomEvent('aacore:cookie-consent-changed', { detail: record })
  );

  return record;
}

export function acceptAllCookies(): CookieConsentRecord {
  return saveCookieConsent(defaultPreferences(true));
}

export function rejectNonEssentialCookies(): CookieConsentRecord {
  return saveCookieConsent(defaultPreferences(false));
}

export function openCookiePreferences(): void {
  window.dispatchEvent(new Event(COOKIE_PREFERENCES_OPEN_EVENT));
}

export function hasAnalyticsConsent(): boolean {
  return getCookieConsent()?.preferences.analytics === true;
}
